import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from './skeleton';
import {
  getProductImage,
  normalizeProductImages,
  resolveSupabaseStoragePublicUrl,
} from '@/lib/image-utils';
import type { ImageProcessingOptions } from '@/types/image';

// ============================================================================
// Types
// ============================================================================

export interface ResponsiveImageProps {
  /** Primary image source URL */
  src?: string;
  /** Alt text for accessibility */
  alt: string;
  /** Product name (used for fallback placeholder generation) */
  productName?: string;
  /** Product category (used for fallback placeholder) */
  category?: string;
  /** Additional image URLs for fallback chain */
  fallbackImages?: string[];
  /** Aspect ratio of the container (e.g., '1/1', '4/3', '16/9') */
  aspectRatio?: string;
  /** How the image should fit within the container */
  objectFit?: 'cover' | 'contain';
  /** Loading priority - 'high' for above-fold, 'low' for lazy loading */
  priority?: 'high' | 'low';
  /** Whether to show a loading skeleton while the image loads */
  showSkeleton?: boolean;
  /** Custom class name for the container */
  className?: string;
  /** Custom class name for the image element */
  imageClassName?: string;
  /** Callback when image loads successfully */
  onLoad?: () => void;
  /** Callback when image fails to load */
  onError?: (error: Error) => void;
  /** Whether to enable hover zoom effect */
  hoverZoom?: boolean;
  /** Image processing options (for future server-side processing) */
  processingOptions?: Partial<ImageProcessingOptions>;
  /** Intersection observer threshold for lazy loading (0-1) */
  lazyThreshold?: number;
}

// ============================================================================
// Component
// ============================================================================

/**
 * ResponsiveImage Component
 *
 * A production-ready image component that ensures consistent display across
 * all viewports with built-in lazy loading, error handling, and fallback chains.
 *
 * Features:
 * - Maintains aspect ratio container to prevent layout shift
 * - Lazy loading with Intersection Observer for below-the-fold images
 * - Automatic fallback to placeholder images
 * - Loading skeleton with smooth transitions
 * - Hover zoom effect (optional)
 * - Error boundary with graceful degradation
 * - Responsive srcset support
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ResponsiveImage
 *   src="https://example.com/product.jpg"
 *   alt="Red Wine Bottle"
 *   aspectRatio="1/1"
 * />
 *
 * // With lazy loading and hover zoom
 * <ResponsiveImage
 *   src={product.images[0]}
 *   alt={product.name}
 *   productName={product.name}
 *   category={product.categoryName}
 *   aspectRatio="1/1"
 *   priority="low"
 *   hoverZoom
 * />
 *
 * // High priority (above the fold)
 * <ResponsiveImage
 *   src={product.images[0]}
 *   alt={product.name}
 *   priority="high"
 *   aspectRatio="1/1"
 * />
 * ```
 */
export const ResponsiveImage = React.forwardRef<HTMLDivElement, ResponsiveImageProps>(
  (
    {
      src,
      alt,
      productName,
      category,
      fallbackImages = [],
      aspectRatio = '1/1',
      objectFit = 'cover',
      priority = 'low',
      showSkeleton = true,
      className,
      imageClassName,
      onLoad,
      onError,
      hoverZoom = false,
      processingOptions,
      lazyThreshold = 0.1,
    },
    ref
  ) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [currentSrc, setCurrentSrc] = useState<string>('');
    const [isInView, setIsInView] = useState(priority === 'high');
    const [retryCount, setRetryCount] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const mergedRef = useMergedRefs(ref, containerRef);
    const resolvedSourceRef = useRef<string>('');

    // Resolve the best image source
    useEffect(() => {
      let resolvedSrc = '';

      if (src && src.trim()) {
        resolvedSrc = resolveSupabaseStoragePublicUrl(src);
      } else if (fallbackImages && fallbackImages.length > 0) {
        resolvedSrc = resolveSupabaseStoragePublicUrl(fallbackImages[0] || '');
      } else if (productName) {
        // Use the existing placeholder system
        const normalizedImages = normalizeProductImages(
          src,
          fallbackImages
        );
        const imageResult = getProductImage(
          normalizedImages,
          productName,
          category,
          { width: 400, height: 400, debug: false }
        );
        resolvedSrc = imageResult.url;
      } else {
        resolvedSrc = getDefaultPlaceholder();
      }

      // Avoid resetting a successfully loaded image when the caller rerenders
      // with a new array identity but the effective source is unchanged.
      if (resolvedSourceRef.current === resolvedSrc) {
        return;
      }

      resolvedSourceRef.current = resolvedSrc;
      setCurrentSrc(resolvedSrc);
      setImageLoaded(false);
      setImageError(false);
      setRetryCount(0);
    }, [src, fallbackImages, productName, category]);

    // Intersection Observer for lazy loading
    useEffect(() => {
      if (priority === 'high') return;

      const element = containerRef.current;
      if (!element) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsInView(true);
              observer.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: '200px', // Start loading 200px before entering viewport
          threshold: lazyThreshold,
        }
      );

      observer.observe(element);

      return () => {
        observer.disconnect();
      };
    }, [priority, lazyThreshold]);

    // Retry loading on error (up to 2 retries)
    const handleRetry = useCallback(() => {
      if (retryCount < 2) {
        setRetryCount((prev) => prev + 1);
        setImageError(false);
        setImageLoaded(false);
        // Force re-render by briefly clearing src
        const current = currentSrc;
        setCurrentSrc('');
        setTimeout(() => setCurrentSrc(current), 50);
      }
    }, [retryCount, currentSrc]);

    const handleImageLoad = useCallback(() => {
      setImageLoaded(true);
      setImageError(false);
      onLoad?.();
    }, [onLoad]);

    const handleImageError = useCallback(() => {
      setImageError(true);

      // Try fallback images before giving up
      const fallbackIndex = retryCount;
      if (fallbackIndex < fallbackImages.length) {
        setCurrentSrc(resolveSupabaseStoragePublicUrl(fallbackImages[fallbackIndex] || ''));
        setRetryCount((prev) => prev + 1);
        setImageLoaded(false);
        return;
      }

      // All fallbacks exhausted - use placeholder
      if (productName) {
        const placeholderUrl = getDefaultPlaceholder(productName, category);
        if (placeholderUrl !== currentSrc) {
          setCurrentSrc(placeholderUrl);
          setImageLoaded(false);
          return;
        }
      }

      onError?.(new Error(`Failed to load image: ${currentSrc}`));
    }, [retryCount, fallbackImages, currentSrc, productName, category, onError]);

    // Map aspect ratio string to Tailwind class
    const aspectClass = getAspectRatioClass(aspectRatio);

    return (
      <div
        ref={mergedRef}
        className={cn(
          'relative overflow-hidden bg-brand-50',
          aspectClass,
          className
        )}
      >
        {/* Loading Skeleton */}
        {showSkeleton && !imageLoaded && !imageError && (
          <Skeleton
            className="absolute inset-0 rounded-none"
            animate
          />
        )}

        {/* Error State */}
        {imageError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-brand-50 text-brand-400">
            <svg
              className="h-10 w-10 mb-2 opacity-60"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
              />
            </svg>
            <span className="text-xs font-medium">Image unavailable</span>
            {retryCount < 2 && (
              <button
                onClick={handleRetry}
                className="mt-1 text-xs text-brand-600 hover:text-brand-700 hover:underline"
              >
                Retry
              </button>
            )}
          </div>
        )}

        {/* Image */}
        {isInView && currentSrc && (
          <img
            src={currentSrc}
            alt={alt}
            {...({ fetchpriority: priority === 'high' ? 'high' : 'low' } as Record<string, string>)}
            className={cn(
              'h-full w-full transition-opacity duration-300',
              objectFit === 'cover' ? 'object-cover' : 'object-contain',
              hoverZoom && 'transition-transform duration-500 group-hover:scale-105',
              imageLoaded ? 'opacity-100' : 'opacity-0',
              imageClassName
            )}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading={priority === 'high' ? 'eager' : 'lazy'}
            decoding="async"
          />
        )}
      </div>
    );
  }
);

ResponsiveImage.displayName = 'ResponsiveImage';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Merge multiple refs into one
 */
function useMergedRefs<T>(
  ...refs: (React.Ref<T> | undefined)[]
): React.RefCallback<T> {
  return useCallback(
    (element: T | null) => {
      refs.forEach((ref) => {
        if (typeof ref === 'function') {
          ref(element);
        } else if (ref && typeof ref === 'object') {
          (ref as React.MutableRefObject<T | null>).current = element;
        }
      });
    },
    [refs]
  );
}

/**
 * Map aspect ratio string to Tailwind aspect-ratio class
 */
function getAspectRatioClass(ratio: string): string {
  const ratioMap: Record<string, string> = {
    '1/1': 'aspect-square',
    '4/3': 'aspect-[4/3]',
    '3/4': 'aspect-[3/4]',
    '16/9': 'aspect-video',
    '3/2': 'aspect-[3/2]',
    '2/3': 'aspect-[2/3]',
    'auto': 'aspect-auto',
  };

  return ratioMap[ratio] || `aspect-[${ratio}]`;
}

/**
 * Get default placeholder image URL
 */
function getDefaultPlaceholder(productName?: string, category?: string): string {
  if (productName) {
    const seed = productName.toLowerCase().replace(/\s+/g, '-').substring(0, 30);
    return `https://picsum.photos/seed/${seed}/400/400`;
  }
  return 'https://placehold.co/400x400/e8ddd8/8b6f5e?text=Product';
}

// ============================================================================
// Exports
// ============================================================================

export default ResponsiveImage;
